import {useState} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Button, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import {OrdersListView} from "../orders/OrdersListView";
import {OrderCreateDialog} from "../dialogs/OrderCreateDialog";
import {useActiveCompanyOrders} from "../../hooks/ordersHooks";
import {useFields} from "../../hooks/fieldsHooks";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ViewAuthorizationGuard} from "../ViewAuthorizationGuard";
import {MainViewLayout} from "../MainViewLayout";
import {CompanyHeader} from "./CompanyHeader";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useCompany} from "../../hooks/companiesHooks";

export const CompanyOrdersView: React.FC = () => {
    const {t} = useTranslation();
    const [orderCreateDialogOpened, setOrderCreateDialogOpened] = useState<boolean>(false);
    const ordersState = useActiveCompanyOrders();
    const access = useAccessMarker(ACCESS_FUNCTIONS);

    const onOrderCreateDialogClose = (isSuccessful: boolean) => {
        setOrderCreateDialogOpened(false);
        if (isSuccessful) {
            ordersState.refresh();
        }
    };

    const companyState = useCompany();
    const companyId = companyState.isSuccess && companyState.data !== undefined ? companyState.data.id : null;
    const fieldsState = useFields({companyId, orderId: undefined, farmId: undefined});
    return (
        <ViewAuthorizationGuard isAuthorized={access.canViewCompanyOrders}>
            <MainViewLayout header={<CompanyHeader basePath="/organization" />}>
                <Stack direction="column">
                    <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            {t("companies.orders_view.orders")}
                        </Typography>
                        <Stack direction="row">
                            <Button startIcon={<AddIcon />} onClick={() => setOrderCreateDialogOpened(true)}>
                                {t("companies.orders_view.new")}
                            </Button>
                        </Stack>
                    </Stack>

                    {companyId && (
                        <OrdersListView
                            orders={ordersState.items}
                            refreshOrders={ordersState.refresh}
                            refreshFields={fieldsState.refresh}
                            fields={fieldsState.items}
                            companyId={companyId}
                        />
                    )}
                </Stack>

                {orderCreateDialogOpened && companyId && (
                    <OrderCreateDialog
                        mode="new"
                        companyId={companyId}
                        open={orderCreateDialogOpened}
                        onClose={onOrderCreateDialogClose}
                    />
                )}
            </MainViewLayout>
        </ViewAuthorizationGuard>
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canViewCompanyOrders"]);
